import type { AnyEventObject } from 'xstate';

import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import { CONFIG_ITEM_KEYS } from '../config-items/config-item-keys';
import { type SelectionType } from '../config-items/config-items';
import { getRequestOptions } from '../request/request-options';
import { startStreaming } from '../request/streaming';
import { getMentionMap } from '../utils/mentions/get-mention-map';

import type { AIExperienceMachineContext } from './get-ai-experience-service';

/**
 * Vary prompt request triggered - if latest response exists, we are interrogating,
 * and do not want to use the original config item (i.e. may possibly be summarise/transform)
 *
 * If it is a generate with tag, the response will be captured within
 * the latest "latest response, prompt", flow.
 *
 * REVISIT THIS: analytics is based on the config item, however
 * interrogation analytics uses base generate instead to complete;
 */
function getConfigItem({ context }: { context: AIExperienceMachineContext }) {
	let configItemToUse = context.configItem;
	if (!editorExperiment('platform_editor_ai_refine_response_button', true)) {
		if (context.baseGenerate && context.latestPromptResponse) {
			// If there is a latestPromptResponse and the context.configItem has a key 'Rovo Agent',
			// then we should use the context.configItem so we can interrogate with the Rovo Agent
			configItemToUse =
				context.configItem && context.configItem.key === CONFIG_ITEM_KEYS.ROVO_AGENT
					? context.configItem
					: context.baseGenerate;
		}
	}
	return configItemToUse;
}

/**
 * Get mention map for the current document
 * We can no longer use the node attrs to get the mention name
 * So we now need to get all the mentions and create a mapping that can be used by the
 * markdown serializer
 */
async function _getMentionMap({ context }: { context: AIExperienceMachineContext }) {
	let mentionMap = {};
	if (context.getMentionNameDetails) {
		mentionMap = await getMentionMap({
			node: context.editorView.state.doc,
			getMentionNameDetails: context.getMentionNameDetails,
		});
	}
	return mentionMap;
}

function hitCache({
	context,
	cacheKey,
}: {
	context: AIExperienceMachineContext;
	cacheKey: string;
}) {
	// Right now we only ever have one object in history as we do not
	// yet support traversing the history object array. This is also to
	// prevent us saving large documents into history multiple times.
	const lastHistoryItem = context.responseHistory.entries[0];
	return (
		lastHistoryItem &&
		cacheKey === lastHistoryItem.cacheKey &&
		context.promptTrigger !== 'interrogate'
	);
}

/**
 * This is responsible for processing the parsed data from
 * streamResponseParser() and passing data into the
 * AIExperienceMachine over in createAIExperienceMachine().
 */
export function streaming(context: AIExperienceMachineContext) {
	async function callbackHandler(callback: (event: AnyEventObject) => void) {
		const abortController = new AbortController();
		const {
			positions,
			userInput,
			configItem: { agent },
		} = context;
		// right now, selectionType is not accurate, some prompts use 'range' for both empty and range, so we use positions to determine if it is empty
		const isEmpty = positions[0] === positions[1];
		const selectionType: SelectionType = isEmpty ? 'empty' : 'range';
		const configItem = getConfigItem({ context });
		const mentionMap = await _getMentionMap({ context });

		const requestOptions = getRequestOptions({
			callback,
			context,
			configItem,
			isEmpty,
			selectionType,
			mentionMap,
		});

		const cacheKey = JSON.stringify({ userInput, agent, requestOptions });

		if (hitCache({ context, cacheKey })) {
			callback({ type: 'same cache key' });
			return;
		}

		await startStreaming({
			abortController,
			cacheKey,
			selectionType,
			product: context.aiProvider.product,
			getFetchCustomHeaders: context.aiProvider.getFetchCustomHeaders,
			channelId: context.channelId,
			callback,
			requestOptions,
		});

		// Cleanup function
		return () => {
			abortController.abort();
		};
	}

	return callbackHandler;
}
